// src/context/AuthContext.tsx
import React, {
  createContext,
  useState,
  useContext,
  ReactNode,
  useEffect,
} from 'react';
import BASE_URL from '../../config/apiConfig';
import { useNavigate } from 'react-router-dom'; // Import useNavigate

// Define user type
interface User {
  email: string;
  password: string;
  // ClientId: number;
}

// Define Auth context type
interface AuthContextType {
  // user: any | null;
  jwt: any | null;
  login: (userData: User) => void;
  registerUser: (userData: any) => void;
  logout: () => void;
}

// Create the AuthContext
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// AuthProvider component
export const AuthProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  // const [user, setUser] = useState<User | null>(null); // Manage authentication state
  // Initialize jwt from sessionStorage if it exists
  const [jwt, setJWT] = useState(() => sessionStorage.getItem('jwt'));

  const navigate = useNavigate();

  useEffect(() => {
    const storedJWT = sessionStorage.getItem('jwt');
    if (storedJWT) {
      setJWT(storedJWT);
    }
  }, []);

  const login = async (userData: User): Promise<boolean> => {
    try {
      const response = await fetch(`${BASE_URL}Auth/Login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(userData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        const message = errorData?.message || 'Login failed';
        throw new Error(message);
      }

      const { token: JWT } = await response.json();
      if (JWT) {
        setJWT(JWT);
        sessionStorage.setItem('jwt', JWT);
        navigate('/main/dashboard');
        return true;
      }

      console.error('JWT token not found in response');
      return false;
    } catch (error: any) {
      console.error('Login error:', error.message);
      if (error.message === 'Email is already in use') {
        throw error; // Allows caller to handle specific cases
      }
      throw new Error('Login failed, please try again later.');
    }
  };

  const registerUser = async (userObj: any): Promise<boolean> => {
    try {
      const response = await fetch(`${BASE_URL}Auth/Register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(userObj),
      });

      if (!response.ok) {
        const errorData = await response.json();
        const message = errorData?.message || 'Registration failed';
        throw new Error(message);
      }

      const { token: JWT } = await response.json();
      if (JWT) {
        setJWT(JWT);
        sessionStorage.setItem('jwt', JWT);
        navigate('/main/dashboard');
        return true;
      }

      console.error('JWT token not found in response');
      return false;
    } catch (error: any) {
      console.error('Registration error:', error.message);
      if (error.message === 'Email is already in use') {
        throw error;
      }
      throw new Error('Registration failed, please try again later.');
    }
  };

  const logout = () => {
    sessionStorage.removeItem('jwt');
    navigate('/');
  };

  return (
    <AuthContext.Provider value={{ jwt, login, registerUser, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook for using auth context
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
